import { type Component, createMemo, Show } from "solid-js";
import { toast } from "solid-sonner";
import { useI18n } from "@/api/i18n";
import { getApiErrorMessage } from "@/api/utils/error";
import { Button } from "@/components/ui/button";
import { useCreatePortalSession } from "../api";
import type { BillingStatusContract } from "../api/types";

type BillingStatusBannerProps = {
  status: BillingStatusContract;
  /** First-charge date (ISO) for a trialing subscription — surfaced in the trial banner. */
  trialEndsAt?: string | null;
};

const formatTrialDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  return new Date(ms).toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const computeGraceDaysRemaining = (endsAt: string | null): number | null => {
  if (!endsAt) return null;
  const diff = new Date(endsAt).getTime() - Date.now();
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  return days > 0 ? days : 0;
};

export const BillingStatusBanner: Component<BillingStatusBannerProps> = (
  props,
) => {
  const { t } = useI18n();
  const portalMutation = useCreatePortalSession();

  const handlePortalRedirect = () => {
    portalMutation
      .mutateAsync(undefined)
      .then((data) => {
        window.location.href = data.portal_url;
      })
      .catch((error: unknown) => {
        toast.error(
          getApiErrorMessage(
            error,
            "Impossibile aprire il portale di fatturazione",
          ),
        );
      });
  };

  const graceDaysRemaining = createMemo(() =>
    computeGraceDaysRemaining(props.status.grace_period_ends_at),
  );

  return (
    <>
      {/* PAST_DUE — soft amber, tone informativo */}
      <Show
        when={
          props.status.billing_status === "past_due" &&
          props.status.has_open_failures &&
          props.status.last_failure
        }
      >
        {(failure) => (
          <div class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
            <span class="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white text-amber-600 text-base shadow-sm">
              ⓘ
            </span>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-gray-900">
                {t("billing.settings.banner.past_due_title", {
                  amount: failure().amount,
                  currency: failure().currency,
                  attempt_number: String(failure().attempt_number),
                })}
              </p>
              <p class="mt-0.5 text-xs text-amber-700">
                {t("billing.settings.banner.past_due_sub")}
              </p>
            </div>
            <Show when={props.status.portal_url_available}>
              <Button
                variant="outline"
                size="sm"
                class="shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100"
                disabled={portalMutation.isPending}
                onClick={handlePortalRedirect}
              >
                {t("billing.settings.banner.past_due_cta")}
              </Button>
            </Show>
          </div>
        )}
      </Show>

      {/* GRACE_PERIOD — amber forte con countdown pill */}
      <Show when={props.status.billing_status === "grace_period"}>
        <div class="rounded-lg border-2 border-amber-400 bg-white px-4 py-3 flex items-start gap-3">
          <span class="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-700 text-base">
            ⚠
          </span>
          <div class="flex-1 min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <p class="text-sm font-semibold text-gray-900">
                {t("billing.settings.banner.grace_period_title")}
              </p>
              <Show when={graceDaysRemaining() !== null}>
                {(days) => (
                  <span class="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white px-2 py-0.5 text-xs font-bold text-amber-700">
                    ⏱{" "}
                    {t("billing.settings.banner.grace_period_days_remaining", {
                      days: String(days()),
                    })}
                  </span>
                )}
              </Show>
            </div>
            <p class="mt-0.5 text-xs text-amber-700">
              {t("billing.settings.banner.grace_period_sub", {
                grace_period_ends_at: props.status.grace_period_ends_at ?? "",
              })}
            </p>
          </div>
          <Show when={props.status.portal_url_available}>
            <Button
              size="sm"
              class="shrink-0"
              disabled={portalMutation.isPending}
              onClick={handlePortalRedirect}
            >
              {t("billing.settings.banner.grace_period_cta")}
            </Button>
          </Show>
        </div>
      </Show>

      {/* UNPAID — rosso, accesso bloccato, CTA primario */}
      <Show when={props.status.billing_status === "unpaid"}>
        <div class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 flex items-start gap-3">
          <span class="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white text-red-600 text-base shadow-sm">
            🚫
          </span>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-red-800">
              {t("billing.settings.banner.unpaid_title")}
            </p>
            <p class="mt-0.5 text-xs text-red-700">
              {t("billing.settings.banner.unpaid_sub")}
            </p>
            <div class="mt-2 flex flex-wrap gap-2">
              <Show when={props.status.portal_url_available}>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={portalMutation.isPending}
                  onClick={handlePortalRedirect}
                >
                  {t("billing.settings.banner.unpaid_cta")}
                </Button>
              </Show>
              <Button
                size="sm"
                variant="outline"
                class="border-red-300 text-red-700 hover:bg-red-100"
              >
                {t("billing.settings.banner.unpaid_support_cta")}
              </Button>
            </div>
          </div>
        </div>
      </Show>

      <Show when={props.status.billing_status === "trialing"}>
        <div class="rounded-md border border-blue-300 bg-blue-50 px-4 py-3">
          <p class="text-sm text-blue-800">
            {t("billing.status_banner.trialing")}
          </p>
          <Show when={formatTrialDate(props.trialEndsAt)}>
            {(date) => (
              <p class="mt-0.5 text-xs font-medium text-blue-700">
                {t("billing.status_banner.trialing_first_charge", {
                  date: date(),
                })}
              </p>
            )}
          </Show>
        </div>
      </Show>
    </>
  );
};
